import { registerIndicator } from 'klinecharts'
import type { Chart } from 'klinecharts'
import { EMPTY_TOOLTIP } from './chartStyles'

/**
 * The walkthrough's time sections, painted BEHIND the candles on the canvas:
 *
 *   ········┃ warm-up │ ░░░ drawings window ░░░ │ trail ┃········
 *            thin line      light tint                   thin line
 *
 * The run's real range is wider than the window around the drawings — the server pads it with
 * indicator warm-up before them and a trail after — and with every candle on the chart the user
 * needs another way to see where each starts and stops. A tint on the window says it without a
 * legend. The run can have a fill and a thin line at each end too, both off for now
 * (`--walkthrough-run-bg` / `--walkthrough-run-border: transparent`); each comes back with its
 * token alone.
 *
 * A custom indicator rather than an overlay, because only an indicator can sit behind the
 * candles: one with a negative zLevel is drawn with `destination-over` (klinecharts 9.8.12,
 * IndicatorView). Like the volume overlay it has no figures, so it contributes nothing to the
 * price axis, and it paints in pixel space. It is engine-only — never in the React indicator
 * list — so persistence, the legend and the pane controls never see it.
 */
export const WT_SECTIONS = 'WT_SECTIONS'

export interface SectionBounds {
  /** The run's real range, ms. */
  rangeStartMs: number
  rangeEndMs: number
  /** The window around the drawings, ms. */
  displayStartMs: number
  displayEndMs: number
}

export interface SectionsExtendData extends SectionBounds {
  runFill: string
  runBorder: string
  windowFill: string
}

/** The run's end lines, px. */
const EDGE_WIDTH = 1

export interface SectionRect {
  kind: 'run' | 'window'
  x: number
  width: number
}

/** First index whose timestamp is >= ms, or `bars.length`. */
function firstAtOrAfter(bars: ReadonlyArray<{ timestamp: number }>, ms: number): number {
  let lo = 0
  let hi = bars.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (bars[mid].timestamp < ms) lo = mid + 1
    else hi = mid
  }
  return lo
}

/**
 * The rectangles to paint, in pixels, for the bars on screen.
 *
 * Each boundary snaps to a bar: a section covers every bar whose open time falls inside it, from
 * that bar's left edge to its right. Nothing is painted past the last bar the chart holds — while
 * the replay is cutting the canvas, the sections stop at the playhead along with the candles.
 */
export function sectionRects(
  bars: ReadonlyArray<{ timestamp: number }>,
  visible: { from: number; to: number },
  bounds: SectionBounds,
  xOf: (dataIndex: number) => number,
  barWidth: number,
  paneWidth: number,
): SectionRect[] {
  if (bars.length === 0) return []
  const { rangeStartMs, displayStartMs, displayEndMs, rangeEndMs } = bounds
  // Half-open [start, end) spans in bar indices.
  const spans: Array<{ kind: SectionRect['kind']; start: number; end: number }> = [
    { kind: 'run', start: firstAtOrAfter(bars, rangeStartMs), end: firstAtOrAfter(bars, displayStartMs) },
    { kind: 'window', start: firstAtOrAfter(bars, displayStartMs), end: firstAtOrAfter(bars, displayEndMs + 1) },
    { kind: 'run', start: firstAtOrAfter(bars, displayEndMs + 1), end: firstAtOrAfter(bars, rangeEndMs + 1) },
  ]
  const half = barWidth / 2
  const out: SectionRect[] = []
  for (const s of spans) {
    // Only what is on screen, give or take the partially clipped bar at either edge.
    const start = Math.max(s.start, visible.from - 1)
    const end = Math.min(s.end, visible.to + 1)
    if (end <= start) continue
    const x0 = Math.max(0, xOf(start) - half)
    const x1 = Math.min(paneWidth, xOf(end - 1) + half)
    if (x1 <= x0) continue
    out.push({ kind: s.kind, x: x0, width: x1 - x0 })
  }
  return out
}

/**
 * Where to draw the lines at the run's two ends: the left x of each `EDGE_WIDTH` line, inside
 * the run.
 *
 * An end is drawn only where the chart holds bars on both sides of it, so there is no line at the
 * playhead while the replay cuts the canvas, nor at the edge of the history loaded so far. It is
 * never clamped to the pane: an end scrolled off screen draws nothing rather than a line at the
 * pane's edge.
 */
export function sectionEdges(
  bars: ReadonlyArray<{ timestamp: number }>,
  bounds: SectionBounds,
  xOf: (dataIndex: number) => number,
  barWidth: number,
  paneWidth: number,
): number[] {
  // Half-open [start, end) span of the run in bar indices, as in sectionRects.
  const start = firstAtOrAfter(bars, bounds.rangeStartMs)
  const end = firstAtOrAfter(bars, bounds.rangeEndMs + 1)
  if (end <= start) return []
  const half = barWidth / 2
  const out: number[] = []
  if (start > 0) out.push(xOf(start) - half)
  if (end < bars.length) out.push(xOf(end - 1) + half - EDGE_WIDTH)
  return out.filter((x) => x >= 0 && x + EDGE_WIDTH <= paneWidth)
}

let registered = false

export function registerWalkthroughSections(): void {
  if (registered) return
  registered = true
  registerIndicator({
    name: WT_SECTIONS,
    shouldOhlc: false,
    figures: [],
    calc: (dataList) => dataList.map(() => ({})),
    draw: ({ ctx, indicator, kLineDataList, visibleRange, bounding, barSpace, xAxis }) => {
      const ext = indicator.extendData as SectionsExtendData | undefined
      if (!ext || indicator.visible === false) return false
      const xOf = (i: number) => xAxis.convertToPixel(i)
      const barWidth = barSpace?.gapBar ?? barSpace?.bar ?? 8
      const rects = sectionRects(kLineDataList, visibleRange, ext, xOf, barWidth, bounding.width)
      const edges = sectionEdges(kLineDataList, ext, xOf, barWidth, bounding.width)
      ctx.save()
      for (const r of rects) {
        ctx.fillStyle = r.kind === 'window' ? ext.windowFill : ext.runFill
        ctx.fillRect(r.x, 0, r.width, bounding.height)
      }
      ctx.fillStyle = ext.runBorder
      for (const x of edges) ctx.fillRect(Math.round(x), 0, EDGE_WIDTH, bounding.height)
      ctx.restore()
      return false
    },
  })
}

/** Paint the sections on the candle pane, or update them if they are already there. */
export function showWalkthroughSections(chart: Chart, data: SectionsExtendData): void {
  registerWalkthroughSections()
  const existing = chart.getIndicatorByPaneId('candle_pane', WT_SECTIONS)
  if (existing) {
    chart.overrideIndicator({ name: WT_SECTIONS, extendData: data }, 'candle_pane')
    return
  }
  chart.createIndicator(
    {
      name: WT_SECTIONS,
      // Behind the candles: negative zLevels are composited `destination-over`.
      zLevel: -1,
      extendData: data,
      createTooltipDataSource: EMPTY_TOOLTIP,
    },
    true,
    { id: 'candle_pane' },
  )
}

export function hideWalkthroughSections(chart: Chart): void {
  if (chart.getIndicatorByPaneId('candle_pane', WT_SECTIONS)) {
    chart.removeIndicator('candle_pane', WT_SECTIONS)
  }
}

/** The sections' colours, read off the design tokens on `el` (they live on `.v1`, not `:root`). */
export function sectionColors(
  el: Element | null,
): Pick<SectionsExtendData, 'runFill' | 'runBorder' | 'windowFill'> {
  const read = (name: string, fallback: string) => {
    if (!el || typeof getComputedStyle !== 'function') return fallback
    return getComputedStyle(el).getPropertyValue(name).trim() || fallback
  }
  return {
    runFill: read('--walkthrough-run-bg', 'transparent'),
    runBorder: read('--walkthrough-run-border', 'transparent'),
    windowFill: read('--walkthrough-window-bg', 'rgba(245,158,11,0.05)'),
  }
}
